import { RELATIVE_PRESETS, rangeDays } from './constants';
import type { DisplayTimeZone, RelativePreset, TimeRange } from './types';

export const getRangeByKey = (key: RelativePreset) =>
  RELATIVE_PRESETS.find(p => p.key === key) ?? null;

export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

/**
 * yyyy-mm-ddThh:mm string for datetime-local in a given tz (or UTC)
 */
export function formatDateTimeInZone(
  date: Date | undefined,
  tz: DisplayTimeZone | undefined
) {
  if (!date || !tz) return '';
  const timeZone = tz === 'browser' ? getBrowserTimeZone() : tz;

  if (timeZone === 'UTC') {
    return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
      date.getUTCDate()
    )}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
  }

  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  const parts = dtf.formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';

  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get(
    'minute'
  )}`;
}

/**
 * UTC label string: yyyy-mm-ddThh:mmZ (always UTC)
 */
export function formatUtcLabel(date: Date | undefined) {
  if (!date) return '';
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate()
  )}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}Z`;
}

/**
 * Parse datetime-local value as wall-clock time in `tz` -> UTC Date
 */
export function parseDateTimeInZone(value: string, tz: DisplayTimeZone) {
  const timeZone = tz === 'browser' ? getBrowserTimeZone() : tz;

  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return null;

  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }

  if (timeZone === 'UTC') {
    return new Date(Date.UTC(y, m - 1, d, hh, mm, 0, 0));
  }

  const utcGuess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0, 0));

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = dtf.formatToParts(utcGuess);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';

  const tzY = Number(get('year'));
  const tzM = Number(get('month'));
  const tzD = Number(get('day'));
  const tzH = Number(get('hour'));
  const tzMin = Number(get('minute'));
  const tzS = Number(get('second'));

  const asIfUtc = Date.UTC(tzY, tzM - 1, tzD, tzH, tzMin, tzS, 0);
  const offsetMs = asIfUtc - utcGuess.getTime();

  return new Date(utcGuess.getTime() - offsetMs);
}

export const getDefaultTimeRange = (tz?: string): TimeRange => {
  const { from, to } = rangeDays(7, tz);
  return { mode: 'relative', preset: '7d', from, to };
};

/**
 * Filter relative presets to only include those whose computed "from" date
 * is at or after the given minDate. This ensures users cannot select time
 * ranges that would extend before the organization was created.
 *
 * Uses exact timestamp comparison so the filter is consistent with the
 * apply-time validation in the TimeRangeSelector component.
 *
 * @param minDate - The earliest date allowed (e.g., organization createdAt)
 * @returns Filtered array of available presets
 */
export function getAvailablePresets(
  minDate: Date | undefined,
  tz?: string
): typeof RELATIVE_PRESETS {
  if (!minDate) return RELATIVE_PRESETS;

  return RELATIVE_PRESETS.filter(preset => {
    const { from } = preset.getRange(tz);
    return from >= minDate;
  });
}

/**
 * Format a UTC ISO datetime string into the given display timezone. This is
 * used by dashboard widgets to render bucket dates in the same timezone
 * selected in the TimeRangeSelector, without introducing any extra libs.
 *
 * You can optionally pass Intl.DateTimeFormat options to tweak the
 * representation for specific use-cases (e.g., date-only vs date+time).
 */
export function formatDateInDisplayTimeZone(
  isoDate: string,
  tz: DisplayTimeZone,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = new Date(isoDate);

  // Resolve 'browser' to the actual local timezone; otherwise the string is
  // treated as an IANA timezone or 'UTC'.
  const timeZone = tz === 'browser' ? getBrowserTimeZone() : tz;

  const baseOptions: Intl.DateTimeFormatOptions = options ?? {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };

  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    ...baseOptions,
  });

  return formatter.format(date);
}
